import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum HostMessageType {
  HOST_SETTINGS = 'HOST_SETTINGS',
  HOST_SETTINGS_UPDATE = 'HOST_SETTINGS_UPDATE',
  HOST_SETTINGS_SUCCESS = 'HOST_SETTINGS_SUCCESS',
  HOST_SETTINGS_ERROR = 'HOST_SETTINGS_ERROR',
  HOST_ASSOCIATE_USER = 'HOST_ASSOCIATE_USER',
  HOST_ASSOCIATE_USER_SUCCESS = 'HOST_ASSOCIATE_USER_SUCCESS',
  HOST_ASSOCIATE_USER_ERROR = 'HOST_ASSOCIATE_USER_ERROR',
  HOST_UNASSOCIATE_USER = 'HOST_UNASSOCIATE_USER',
  HOST_UNASSOCIATE_USER_SUCCESS = 'HOST_UNASSOCIATE_USER_SUCCESS',
  HOST_UNASSOCIATE_USER_ERROR = 'HOST_UNASSOCIATE_USER_ERROR',
  HOST_MACHINE_CREDENTIALS = 'HOST_MACHINE_CREDENTIALS',
  HOST_SCREENSHOT_GRAB_COMMAND = 'HOST_SCREENSHOT_GRAB_COMMAND',
  HOST_SCREENSHOT_GRAB_COMPLETE = 'HOST_SCREENSHOT_GRAB_COMPLETE',
  HOST_SCHEDULED_SCREENSHOT_GRAB_COMMAND = 'HOST_SCHEDULED_SCREENSHOT_GRAB_COMMAND',
  HOST_SCHEDULED_SCREENSHOT_GRAB_COMPLETE = 'HOST_SCHEDULED_SCREENSHOT_GRAB_COMPLETE',
  /** Operator-requested teardown of Nonsole/guest mode (no reboot). */
  HOST_EXIT_NONSOLE = 'HOST_EXIT_NONSOLE',
  /** Host reports a scheduled watcher hash mismatch that triggered a game restart. */
  HOST_SCHEDULED_WATCHER_RESTART_REPORT = 'HOST_SCHEDULED_WATCHER_RESTART_REPORT',
  /** Host reports the result of a scheduled watcher hash check (pass or fail). */
  HOST_SCHEDULED_WATCHER_CHECK_REPORT = 'HOST_SCHEDULED_WATCHER_CHECK_REPORT',
}

export type HostSettings = z.infer<typeof hostSettingsPayloadSchema>;

// User profile schema for future use
const userProfileSchema = z.object({
  username: z.string().optional(),
  email: z.string().email().optional(),
  profileId: z.string().optional(),
});

// Payload schemas
const hostSettingsPayloadSchema = z.object({
  displayName: z.string().optional(),
  maxPlayerCount: z.number().optional(),
  version: z.string().optional(),
  frontendVersion: z.string().optional(),
});

const hostSettingsUpdatePayloadSchema = z.object({
  machineId: z.string(),
  settings: hostSettingsPayloadSchema,
});

const hostSettingsSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const hostSettingsErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const hostAssociateUserPayloadSchema = z.object({
  machineId: z.string(),
  userProfile: userProfileSchema,
});

const hostAssociateUserSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const hostAssociateUserErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const hostUnassociateUserPayloadSchema = z.object({
  machineId: z.string(),
  userProfile: userProfileSchema,
});

const hostUnassociateUserSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const hostUnassociateUserErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const hostMachineCredentialsPayloadSchema = z.object({
  machineId: z.string(),
  epic: z.object({
    username: z.string(),
    password: z.string(),
  }).optional(),
  steam: z.object({
    username: z.string(),
    password: z.string(),
  }).optional(),
});

const hostScreenshotGrabCommandPayloadSchema = z.object({
  uploadUrl: z.string().url(),
  requestId: z.string(),
});

// Runtime payload schema stays a loose z.object so the top-level
// `hostMessageSchema` can keep using `z.discriminatedUnion('type', ...)` —
// Zod's `.merge()` only returns a ZodObject when both sides are objects, and
// the top-level discriminator requires each branch to BE a ZodObject. The
// strict success/failure invariant is enforced on the **construction** side
// via the hand-typed `HostScreenshotGrabCompletePayload` union exported
// further below, which is what `generateHostMessage()` accepts. Invalid
// shapes can therefore never be produced in the first place; incoming WS
// messages always originate from a `generateHostMessage()` call upstream.
const hostScreenshotGrabCompletePayloadSchema = z.object({
  requestId: z.string(),
  success: z.boolean(),
  sizeBytes: z.number().optional(),
  error: z.string().optional(),
});

const hostExitNonsolePayloadSchema = z.object({
  reason: z.string().optional(),
});

const hostScheduledWatcherRestartReportPayloadSchema = z.object({
  machineId: z.string(),
  campaignId: z.string(),
  expectedHash: z.string(),
  actualHash: z.string(),
  hammingDistance: z.number(),
  tolerance: z.number(),
  restartedAt: z.number(),
});

const hostScheduledWatcherCheckReportPayloadSchema = z.object({
  machineId: z.string(),
  campaignId: z.string(),
  passed: z.boolean(),
  hammingDistance: z.number(),
  tolerance: z.number(),
  checkedAt: z.number(),
});

// Full message schemas
const hostSettingsSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SETTINGS),
  })
  .merge(hostSettingsPayloadSchema);

const hostSettingsUpdateSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SETTINGS_UPDATE),
  })
  .merge(hostSettingsUpdatePayloadSchema);

const hostSettingsSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SETTINGS_SUCCESS),
  })
  .merge(hostSettingsSuccessPayloadSchema);

const hostSettingsErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SETTINGS_ERROR),
  })
  .merge(hostSettingsErrorPayloadSchema);

const hostAssociateUserSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_ASSOCIATE_USER),
  })
  .merge(hostAssociateUserPayloadSchema);

const hostAssociateUserSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_ASSOCIATE_USER_SUCCESS),
  })
  .merge(hostAssociateUserSuccessPayloadSchema);

const hostAssociateUserErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_ASSOCIATE_USER_ERROR),
  })
  .merge(hostAssociateUserErrorPayloadSchema);

const hostUnassociateUserSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_UNASSOCIATE_USER),
  })
  .merge(hostUnassociateUserPayloadSchema);

const hostUnassociateUserSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_UNASSOCIATE_USER_SUCCESS),
  })
  .merge(hostUnassociateUserSuccessPayloadSchema);

const hostUnassociateUserErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_UNASSOCIATE_USER_ERROR),
  })
  .merge(hostUnassociateUserErrorPayloadSchema);

const hostMachineCredentialsSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_MACHINE_CREDENTIALS),
  })
  .merge(hostMachineCredentialsPayloadSchema);

const hostScreenshotGrabCommandSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SCREENSHOT_GRAB_COMMAND),
  })
  .merge(hostScreenshotGrabCommandPayloadSchema);

const hostScreenshotGrabCompleteSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SCREENSHOT_GRAB_COMPLETE),
  })
  .merge(hostScreenshotGrabCompletePayloadSchema);

const hostScheduledScreenshotGrabCommandSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SCHEDULED_SCREENSHOT_GRAB_COMMAND),
  })
  .merge(hostScreenshotGrabCommandPayloadSchema);

const hostScheduledScreenshotGrabCompleteSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SCHEDULED_SCREENSHOT_GRAB_COMPLETE),
  })
  .merge(hostScreenshotGrabCompletePayloadSchema);

const hostExitNonsoleSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_EXIT_NONSOLE),
  })
  .merge(hostExitNonsolePayloadSchema);

const hostScheduledWatcherRestartReportSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SCHEDULED_WATCHER_RESTART_REPORT),
  })
  .merge(hostScheduledWatcherRestartReportPayloadSchema);

const hostScheduledWatcherCheckReportSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SCHEDULED_WATCHER_CHECK_REPORT),
  })
  .merge(hostScheduledWatcherCheckReportPayloadSchema);

export const hostMessageSchema = z.discriminatedUnion('type', [
  hostSettingsSchema,
  hostSettingsSuccessSchema,
  hostSettingsErrorSchema,
  hostSettingsUpdateSchema,
  hostAssociateUserSchema,
  hostAssociateUserSuccessSchema,
  hostAssociateUserErrorSchema,
  hostUnassociateUserSchema,
  hostUnassociateUserSuccessSchema,
  hostUnassociateUserErrorSchema,
  hostMachineCredentialsSchema,
  hostScreenshotGrabCommandSchema,
  hostScreenshotGrabCompleteSchema,
  hostScheduledScreenshotGrabCommandSchema,
  hostScheduledScreenshotGrabCompleteSchema,
  hostExitNonsoleSchema,
  hostScheduledWatcherRestartReportSchema,
  hostScheduledWatcherCheckReportSchema,
]);

// Payload types
export type HostSettingsPayload = z.infer<typeof hostSettingsPayloadSchema>;
export type HostSettingsUpdatePayload = z.infer<typeof hostSettingsUpdatePayloadSchema>;
export type HostSettingsSuccessPayload = z.infer<typeof hostSettingsSuccessPayloadSchema>;
export type HostSettingsErrorPayload = z.infer<typeof hostSettingsErrorPayloadSchema>;
export type HostAssociateUserPayload = z.infer<typeof hostAssociateUserPayloadSchema>;
export type HostAssociateUserSuccessPayload = z.infer<typeof hostAssociateUserSuccessPayloadSchema>;
export type HostAssociateUserErrorPayload = z.infer<typeof hostAssociateUserErrorPayloadSchema>;
export type HostUnassociateUserPayload = z.infer<typeof hostUnassociateUserPayloadSchema>;
export type HostUnassociateUserSuccessPayload = z.infer<typeof hostUnassociateUserSuccessPayloadSchema>;
export type HostUnassociateUserErrorPayload = z.infer<typeof hostUnassociateUserErrorPayloadSchema>;
export type HostMachineCredentialsPayload = z.infer<typeof hostMachineCredentialsPayloadSchema>;
export type HostScreenshotGrabCommandPayload = z.infer<typeof hostScreenshotGrabCommandPayloadSchema>;
/**
 * Strict discriminated union — see comment on
 * `hostScreenshotGrabCompletePayloadSchema` for why the schema is loose at
 * runtime while the TS contract is tight. Callers (chiefly
 * `generateHostMessage(HOST_SCREENSHOT_GRAB_COMPLETE, …)`) must construct a
 * coherent shape; downstream consumers narrow on `payload.success`.
 */
export type HostScreenshotGrabCompletePayload =
  | { requestId: string; success: true; sizeBytes: number }
  | { requestId: string; success: false; error: string };
export type HostExitNonsolePayload = z.infer<typeof hostExitNonsolePayloadSchema>;
export type HostScheduledWatcherRestartReportPayload = z.infer<typeof hostScheduledWatcherRestartReportPayloadSchema>;
export type HostScheduledWatcherCheckReportPayload = z.infer<typeof hostScheduledWatcherCheckReportPayloadSchema>;

// Full message type
export type HostMessage = z.infer<typeof hostMessageSchema>;

type PayloadType<T extends HostMessageType> = T extends HostMessageType.HOST_SETTINGS
  ? HostSettingsPayload
  : T extends HostMessageType.HOST_SETTINGS_UPDATE
    ? HostSettingsUpdatePayload
    : T extends HostMessageType.HOST_SETTINGS_SUCCESS
      ? HostSettingsSuccessPayload
      : T extends HostMessageType.HOST_SETTINGS_ERROR
        ? HostSettingsErrorPayload
        : T extends HostMessageType.HOST_ASSOCIATE_USER
          ? HostAssociateUserPayload
          : T extends HostMessageType.HOST_ASSOCIATE_USER_SUCCESS
            ? HostAssociateUserSuccessPayload
            : T extends HostMessageType.HOST_ASSOCIATE_USER_ERROR
              ? HostAssociateUserErrorPayload
              : T extends HostMessageType.HOST_UNASSOCIATE_USER
                ? HostUnassociateUserPayload
                : T extends HostMessageType.HOST_UNASSOCIATE_USER_SUCCESS
                  ? HostUnassociateUserSuccessPayload
                  : T extends HostMessageType.HOST_UNASSOCIATE_USER_ERROR
                    ? HostUnassociateUserErrorPayload
                    : T extends HostMessageType.HOST_MACHINE_CREDENTIALS
                      ? HostMachineCredentialsPayload
                      : T extends HostMessageType.HOST_SCREENSHOT_GRAB_COMMAND
                        ? HostScreenshotGrabCommandPayload
                        : T extends HostMessageType.HOST_SCREENSHOT_GRAB_COMPLETE
                          ? HostScreenshotGrabCompletePayload
                          : T extends HostMessageType.HOST_SCHEDULED_SCREENSHOT_GRAB_COMMAND
                            ? HostScreenshotGrabCommandPayload
                            : T extends HostMessageType.HOST_SCHEDULED_SCREENSHOT_GRAB_COMPLETE
                              ? HostScreenshotGrabCompletePayload
                              : T extends HostMessageType.HOST_EXIT_NONSOLE
                                ? HostExitNonsolePayload
                                : T extends HostMessageType.HOST_SCHEDULED_WATCHER_RESTART_REPORT
                                  ? HostScheduledWatcherRestartReportPayload
                                  : T extends HostMessageType.HOST_SCHEDULED_WATCHER_CHECK_REPORT
                                    ? HostScheduledWatcherCheckReportPayload
                                    : never;

export function generateHostMessage<T extends HostMessageType>(type: T, payload: PayloadType<T>): HostMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case HostMessageType.HOST_SETTINGS:
      return hostSettingsSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SETTINGS_UPDATE:
      return hostSettingsUpdateSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SETTINGS_SUCCESS:
      return hostSettingsSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SETTINGS_ERROR:
      return hostSettingsErrorSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_ASSOCIATE_USER:
      return hostAssociateUserSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_ASSOCIATE_USER_SUCCESS:
      return hostAssociateUserSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_ASSOCIATE_USER_ERROR:
      return hostAssociateUserErrorSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_UNASSOCIATE_USER:
      return hostUnassociateUserSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_UNASSOCIATE_USER_SUCCESS:
      return hostUnassociateUserSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_UNASSOCIATE_USER_ERROR:
      return hostUnassociateUserErrorSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_MACHINE_CREDENTIALS:
      return hostMachineCredentialsSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SCREENSHOT_GRAB_COMMAND:
      return hostScreenshotGrabCommandSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SCREENSHOT_GRAB_COMPLETE:
      return hostScreenshotGrabCompleteSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SCHEDULED_SCREENSHOT_GRAB_COMMAND:
      return hostScheduledScreenshotGrabCommandSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SCHEDULED_SCREENSHOT_GRAB_COMPLETE:
      return hostScheduledScreenshotGrabCompleteSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_EXIT_NONSOLE:
      return hostExitNonsoleSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SCHEDULED_WATCHER_RESTART_REPORT:
      return hostScheduledWatcherRestartReportSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SCHEDULED_WATCHER_CHECK_REPORT:
      return hostScheduledWatcherCheckReportSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
